import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import useAuthStore from "../store/authStore";
import "./ProductList.css";

const API_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3337";

const Tuyauterie = () => {
  const [productsList, setProductsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const { role, isAuthenticated, token } = useAuthStore();
  const toolbarRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    toolbarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/tuyauterie`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProductsList(response.data);
      } catch (err) {
        console.error("Erreur lors de la récupération des produits :", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [token]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const filteredProducts = productsList.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;
    try {
      setDeletingId(productId);
      await axios.delete(`${API_URL}/api/tuyauterie/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProductsList((prev) => prev.filter((p) => p._id !== productId));
    } catch (err) {
      console.error("Erreur suppression produit :", err);
      alert("Une erreur est survenue lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <section className="pl-hero">
        <div className="pl-hero__content">
          <h1 className="pl-hero__title">Tuyauterie</h1>
          <div className="pl-hero__breadcrumb">
            <Link to="/">Accueil</Link>
            <span>/</span>
            <Link to="/produits">Produits</Link>
            <span>/</span>
            <span>Tuyauterie</span>
          </div>
        </div>
      </section>

      <div className="pl-body">
        <aside className="pl-sidebar">
          <div className="pl-sidebar__card">
            <div className="pl-sidebar__title">
              <i className="fas fa-search" /> Recherche
            </div>
            <div className="pl-search">
              <i className="fas fa-search pl-search__icon" />
              <input
                type="text"
                className="pl-search__input"
                placeholder="Rechercher un produit…"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </aside>

        <main className="pl-main">
          <div className="pl-toolbar" ref={toolbarRef}>
            <div className="pl-toolbar__count">
              <strong>{filteredProducts.length}</strong> produit{filteredProducts.length !== 1 ? "s" : ""} trouvé{filteredProducts.length !== 1 ? "s" : ""}
            </div>
            {searchTerm && (
              <button className="pl-toolbar__clear" onClick={clearFilters}>
                <i className="fas fa-times" /> Effacer les filtres
              </button>
            )}
          </div>

          {loading ? (
            <div className="pl-loading"><div className="pl-spinner" /></div>
          ) : currentProducts.length > 0 ? (
            <>
              <div className="pl-grid">
                {currentProducts.map((product) => (
                  <div key={product._id} className="pl-card">
                    <Link to={`/tuyauterie/${product._id}`} style={{ textDecoration: "none", color: "inherit" }}>
                      <div className="pl-card__img-wrap">
                        <img
                          className="pl-card__img"
                          src={product.image?.startsWith?.("http") ? product.image : `${API_URL}${product.image}`}
                          alt={product.name}
                        />
                      </div>
                      <div className="pl-card__body">
                        <div className="pl-card__name">{product.name}</div>
                      </div>
                    </Link>
                    <div className="pl-card__actions">
                      <Link to={`/tuyauterie/${product._id}`} className="pl-card__link">
                        Voir détails <i className="fas fa-arrow-right" />
                      </Link>
                      {isAuthenticated && role === "admin" && (
                        <button
                          className="pl-card__delete"
                          onClick={() => handleDeleteProduct(product._id)}
                          disabled={deletingId === product._id}
                          title="Supprimer"
                        >
                          {deletingId === product._id ? <div className="pl-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : <i className="fas fa-trash" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="pl-pagination">
                  <button className="pl-pagination__btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <i className="fas fa-chevron-left" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button key={page} className={`pl-pagination__btn ${page === currentPage ? "pl-pagination__btn--active" : ""}`} onClick={() => setCurrentPage(page)}>
                      {page}
                    </button>
                  ))}
                  <button className="pl-pagination__btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                    <i className="fas fa-chevron-right" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="pl-empty">
              <div className="pl-empty__icon"><i className="fas fa-box-open" /></div>
              <div className="pl-empty__text">Aucun produit trouvé</div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Tuyauterie;
